import { createAppDefaults } from '@eventbrite/bsp-sdk';
import { useOrionSettings } from '@eventbrite/bsp-sdk/react';
import { Stage } from '@eventbrite/webapp-constants';
import { useEffect, useMemo, useState } from 'react';

export type HeaderCreatorVariant =
  | 'control'
  | 'direct-signup'
  | 'restore-2025-layout'
  | 'orange-direct-signup';

type CreatorMlpsSettings = {
  header_create_events_direct_signup: boolean;
  header_use_2025_layout: boolean;
  header_creator_cta_orange: boolean;
  header_organizer_create_events_cta: boolean;
  header_organizer_get_started_orange: boolean;
};

const DEFAULT_SETTINGS = createAppDefaults<CreatorMlpsSettings>({
  header_create_events_direct_signup: false,
  header_use_2025_layout: false,
  header_creator_cta_orange: false,
  header_organizer_create_events_cta: false,
  header_organizer_get_started_orange: false,
});

const HEADER_CREATE_EVENTS_SETTING_KEYS: Array<keyof CreatorMlpsSettings> = [
  'header_create_events_direct_signup',
  'header_use_2025_layout',
  'header_creator_cta_orange',
  'header_organizer_create_events_cta',
  'header_organizer_get_started_orange',
];

const bspEnvironmentFromStage = (stage: Stage): 'staging' | 'development' | 'production' => {
  if (process.env.NODE_ENV === 'development') {
    return 'development';
  }
  if (stage === Stage.TEST) {
    return 'staging';
  }
  if (stage === Stage.DEV || stage === Stage.LOCAL) {
    return 'development';
  }
  return 'production';
};

export const useBspConfig = (stage: Stage) =>
  useMemo(
    () =>
      ({
        appName: 'eventbrite_web',
        environment: bspEnvironmentFromStage(stage),
        defaultOrionSettings: DEFAULT_SETTINGS,
        defaultSegmentations: {},
      }) as const,
    [stage],
  );

const isEnabled = (settings: Record<string, unknown>, setting: keyof CreatorMlpsSettings) =>
  settings[setting] === true;

export const resolveHeaderCreatorVariant = (
  rawSettings: unknown,
  isLoading: boolean,
  isServer = typeof window === 'undefined',
): HeaderCreatorVariant => {
  if (isLoading || isServer || !rawSettings || typeof rawSettings !== 'object') {
    return 'control';
  }

  const settings = rawSettings as Record<string, unknown>;
  if (
    HEADER_CREATE_EVENTS_SETTING_KEYS.some(
      (setting) => settings[setting] !== undefined && typeof settings[setting] !== 'boolean',
    )
  ) {
    return 'control';
  }
  const directSignup = isEnabled(settings, 'header_create_events_direct_signup');
  const use2025Layout = isEnabled(settings, 'header_use_2025_layout');
  const orangeCreatorCta = isEnabled(settings, 'header_creator_cta_orange');
  const organizerCreateEvents = isEnabled(settings, 'header_organizer_create_events_cta');
  const orangeGetStarted = isEnabled(settings, 'header_organizer_get_started_orange');

  if (
    !directSignup &&
    use2025Layout &&
    !orangeCreatorCta &&
    organizerCreateEvents &&
    !orangeGetStarted
  ) {
    return 'restore-2025-layout';
  }

  if (
    directSignup &&
    !use2025Layout &&
    orangeCreatorCta &&
    organizerCreateEvents &&
    orangeGetStarted
  ) {
    return 'orange-direct-signup';
  }

  return 'control';
};

export const useHeaderCreatorVariant = (): HeaderCreatorVariant => {
  const [hasHydrated, setHasHydrated] = useState(false);
  useEffect(() => {
    setHasHydrated(true);
  }, []);

  let settings: unknown = DEFAULT_SETTINGS;
  let isLoading = false;

  try {
    const state = useOrionSettings<CreatorMlpsSettings>();
    settings = state.settings;
    isLoading = state.isLoading;
  } catch {
    return 'control';
  }

  // Preserve the SSR control markup through the first client render.
  return resolveHeaderCreatorVariant(settings, isLoading, !hasHydrated);
};
